import type { CashFlowAnalysis, MoneyJson } from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatCop,
  MoneyText,
  toast,
} from '@paguz/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  MessageSquareTextIcon,
  PencilIcon,
  UserCheckIcon,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AdminApplicationDetail } from '../../api/origination-schemas.ts';
import { Field, Textarea } from '../../components/form-controls.tsx';
import { Panel } from '../../components/page.tsx';
import { formatRate } from '../../lib/format.ts';
import { CASH_FLOW_METHOD, EVIDENCE_LABEL } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { MoneyField } from '../config/setting-editor.tsx';
import { SECTION_LABEL } from './labels.ts';

const route = getRouteApi('/app/expedientes/$applicationId');
type Item = CashFlowAnalysis['items'][number];

const SECTION_ORDER = [
  'SALES',
  'COGS',
  'OPEX',
  'OTHER_INCOME',
  'HOUSEHOLD',
  'DEBT',
  'ASSET',
  'LIABILITY',
];
const OUTFLOW = new Set(['COGS', 'OPEX', 'HOUSEHOLD', 'DEBT']);

/**
 * Flujo de caja construido por el agente: cada cifra enlaza a su fuente (documento o turno de la
 * conversación) y a su método. Las cifras derivadas vienen del servidor (credit-math), nunca de la UI.
 */
export function CashFlowTab({ detail }: { detail: AdminApplicationDetail }) {
  const cf = detail.cashFlow;
  const [adjusting, setAdjusting] = useState<Item | null>(null);
  if (!cf)
    return (
      <Panel>
        <p className="text-sm text-muted-foreground">
          El Asesor IA aún no construye el flujo de caja (el expediente está en{' '}
          {detail.application.phase}).
        </p>
      </Panel>
    );
  return (
    <div className="flex flex-col gap-6">
      <Indicators cf={cf} />
      <Panel
        title="Estado de resultados simplificado (mensual)"
        description={`Versión ${cf.version} · ${cf.periodMonths} meses analizados · confianza ${{ LOW: 'baja', MEDIUM: 'media', HIGH: 'alta' }[cf.confidence]}. Abre una partida para ver sus fuentes.`}
        bodyClassName="p-0"
      >
        <ItemsTable cf={cf} detail={detail} onAdjust={setAdjusting} />
      </Panel>
      <Panel title="Supuestos y método">
        <ul className="list-disc pl-5 text-sm">
          {cf.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </Panel>
      <AdjustDialog
        detail={detail}
        item={adjusting}
        onClose={() => {
          setAdjusting(null);
        }}
      />
    </div>
  );
}

function Indicators({ cf }: { cf: CashFlowAnalysis }) {
  const tiles: { label: string; value: MoneyJson; hint?: string; strong?: boolean }[] = [
    { label: 'Ventas mensuales', value: cf.monthlySales },
    {
      label: 'Ingreso neto del negocio',
      value: cf.businessNetIncome,
      hint: `Margen bruto ${formatCop(cf.grossMargin.amount)}`,
    },
    { label: 'Gastos del hogar', value: cf.householdExpenses },
    { label: 'Cuotas vigentes', value: cf.existingDebtPayments },
    { label: 'Capacidad de pago', value: cf.paymentCapacity, strong: true },
    {
      label: 'Cuota máxima',
      value: cf.maxInstallment,
      hint: `Factor prudencial ${formatRate(cf.prudentialFactor, '', 0)}`,
      strong: true,
    },
    { label: 'Cupo sugerido', value: cf.suggestedLimit, strong: true },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={cn(
            'rounded-2xl border border-border bg-card p-4',
            t.strong && 'border-highlight/40 bg-highlight-soft/40',
          )}
        >
          <p className="text-xs font-semibold text-muted-foreground">{t.label}</p>
          <MoneyText
            value={t.value.amount}
            size="lg"
            className="mt-1 font-semibold text-foreground"
          />
          {t.hint && <p className="text-xs text-muted-foreground">{t.hint}</p>}
        </div>
      ))}
    </div>
  );
}

function ItemsTable({
  cf,
  detail,
  onAdjust,
}: {
  cf: CashFlowAnalysis;
  detail: AdminApplicationDetail;
  onAdjust: (item: Item) => void;
}) {
  const [open, setOpen] = useState<string | null>(cf.items[0]?.id ?? null);
  const canAdjust = useCan('applications:operate') && detail.application.status === 'SUBMITTED';
  const navigate = route.useNavigate();
  const docs = new Map(detail.application.documents.map((d) => [d.id, d]));
  const sections = SECTION_ORDER.filter((s) => cf.items.some((i) => i.section === s));
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-border text-left text-xs text-muted-foreground">
        <tr>
          <th className="px-5 py-2 font-semibold">Partida</th>
          <th className="px-3 py-2 font-semibold">Método</th>
          <th className="px-3 py-2 font-semibold">Fuentes</th>
          <th className="px-5 py-2 text-right font-semibold">Mensual</th>
          <th className="w-20" />
        </tr>
      </thead>
      <tbody>
        {sections.map((section) => (
          <Fragment key={section}>
            <tr className="bg-muted/60">
              <td
                colSpan={5}
                className="px-5 py-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
              >
                {SECTION_LABEL[section]}
              </td>
            </tr>
            {cf.items
              .filter((i) => i.section === section)
              .map((it) => {
                const expanded = open === it.id;
                return (
                  <Fragment key={it.id}>
                    <tr className="border-b border-border">
                      <td className="px-5 py-2.5">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          className="flex items-center gap-1.5 text-left font-semibold text-foreground hover:text-primary"
                          onClick={() => {
                            setOpen(expanded ? null : it.id);
                          }}
                        >
                          {expanded ? (
                            <ChevronDownIcon className="size-4" />
                          ) : (
                            <ChevronRightIcon className="size-4" />
                          )}
                          {it.label}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge
                          variant={
                            it.method === 'TRIANGULATED' || it.method === 'DOCUMENT'
                              ? 'success'
                              : 'secondary'
                          }
                        >
                          {CASH_FLOW_METHOD[it.method]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{it.sources.length}</td>
                      <td className="px-5 py-2.5 text-right">
                        <MoneyText
                          value={it.monthlyAmount.amount}
                          size="sm"
                          className={cn(
                            'font-semibold',
                            OUTFLOW.has(it.section) && 'text-muted-foreground',
                          )}
                        />
                      </td>
                      <td className="pr-4 text-right">
                        {canAdjust && (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Ajustar ${it.label}`}
                            onClick={() => {
                              onAdjust(it);
                            }}
                          >
                            <PencilIcon />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-b border-border bg-highlight-soft/20">
                        <td colSpan={5} className="px-5 py-3">
                          <ul className="flex flex-col gap-2">
                            {it.sources.map((s, idx) => {
                              const doc = s.documentId ? docs.get(s.documentId) : undefined;
                              const analyst =
                                !s.documentId && !s.memoryFactId && s.excerpt.startsWith('Ajuste');
                              return (
                                <li key={idx} className="flex items-start gap-3">
                                  <span className="mt-0.5 text-muted-foreground">
                                    {doc ? (
                                      <FileTextIcon className="size-4" />
                                    ) : analyst ? (
                                      <UserCheckIcon className="size-4" />
                                    ) : (
                                      <MessageSquareTextIcon className="size-4" />
                                    )}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p>{s.excerpt}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Peso {formatRate(s.weight, '', 0)}
                                      {s.memoryFactId && ` · hecho de memoria ${s.memoryFactId}`}
                                    </p>
                                  </div>
                                  {doc && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      aria-label={`Ver ${EVIDENCE_LABEL[doc.declaredType ?? 'OTHER'].toLowerCase()}`}
                                      onClick={() => {
                                        void navigate({
                                          search: { tab: 'documentos', doc: doc.id },
                                        });
                                      }}
                                    >
                                      Ver soporte
                                    </Button>
                                  )}
                                </li>
                              );
                            })}
                            {it.sources.length === 0 && (
                              <li className="text-muted-foreground">Sin fuentes registradas.</li>
                            )}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function AdjustDialog({
  detail,
  item,
  onClose,
}: {
  detail: AdminApplicationDetail;
  item: Item | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<MoneyJson | null>(null);
  const [reason, setReason] = useState('');
  const value = amount ?? item?.monthlyAmount ?? { amount: '0.00', currency: 'COP' as const };
  const mutation = useMutation({
    mutationFn: () =>
      api.adjustCashFlowItem(detail.application.id, item?.id ?? '', {
        monthlyAmount: value,
        reason: reason.trim(),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.application(detail.application.id), next);
      toast.success('Cifra ajustada; el flujo se recalculó en el servidor');
      close();
    },
    onError: (error) => toast.error(describeError(error)),
  });
  const close = () => {
    setAmount(null);
    setReason('');
    onClose();
  };
  return (
    <Dialog
      open={item !== null}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar {item?.label.toLowerCase()}</DialogTitle>
          <DialogDescription>
            El ajuste queda como una fuente más de la partida (con tu nombre y motivo) y el motor
            recalcula capacidad de pago y cuota máxima.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <MoneyField label="Nuevo monto mensual" value={value} onChange={setAmount} />
          <Field label="Motivo (queda en auditoría)" htmlFor="adj-reason">
            <Textarea
              id="adj-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button
            variant="highlight"
            disabled={
              reason.trim().length < 5 ||
              mutation.isPending ||
              value.amount === item?.monthlyAmount.amount
            }
            onClick={() => {
              mutation.mutate();
            }}
          >
            Ajustar y recalcular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
